import { lazy, Suspense, useEffect } from 'react'
import { useAuth } from 'react-oidc-context'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'

import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { SessionExpiredDialog } from '@/components/auth/SessionExpiredDialog'
import { SessionExpiryWarningDialog } from '@/components/auth/SessionExpiryWarningDialog'
import { TokenMonitor } from '@/components/auth/TokenMonitor'
import { TokenSync } from '@/components/auth/TokenSync'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { LazyErrorFallback } from '@/components/ui/LazyErrorFallback'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { LOYALTY_EDIT_SECTIONS, LOYALTY_VIEW_SECTIONS } from '@/constants/sections'
import { AppLayout } from '@/layouts/AppLayout'
import { setOnUnauthorized } from '@/lib/http/interceptors'
import { INCOMING_TRANSFERS_PATH, RECEIVED_TRANSFERS_PATH } from '@/lib/routes/inventory-transfers'
import { MISSING_SCANS_PATH } from '@/lib/routes/missing-scans'
import { REPORT_JOBS_PATH } from '@/lib/routes/report-jobs'
import { TRANSFER_AUDITS_PATH } from '@/lib/routes/transfer-audits'
import { CreatePosUserPage } from '@/pages/admin/CreatePosUserPage'
import { EditPosUserPage } from '@/pages/admin/EditPosUserPage'
import { PosUserDetailPage } from '@/pages/admin/PosUserDetailPage'
import { CallbackPage } from '@/pages/auth/CallbackPage'
import { DebugPage } from '@/pages/DebugPage'
import { HomePage } from '@/pages/HomePage'
import { LoginPage } from '@/pages/LoginPage'
import { CreateUserPage } from '@/pages/operations/CreateUserPage'
import { EditUserPage } from '@/pages/operations/EditUserPage'
import { SystemSelectionPage } from '@/pages/SystemSelectionPage'
import { useAuthStore } from '@/stores/auth-store'

const CreateDiscountPage = lazy(() =>
  import('@/pages/admin/CreateDiscountPage').then((m) => ({ default: m.CreateDiscountPage }))
)
const EditDiscountPage = lazy(() =>
  import('@/pages/admin/EditDiscountPage').then((m) => ({ default: m.EditDiscountPage }))
)
const CreateMessagePage = lazy(() =>
  import('@/pages/admin/CreateMessagePage').then((m) => ({ default: m.CreateMessagePage }))
)
const EditLoyaltyCustomerPage = lazy(() =>
  import('@/pages/admin/EditLoyaltyCustomerPage').then((m) => ({
    default: m.EditLoyaltyCustomerPage,
  }))
)
const LoyaltyCustomerDetailPage = lazy(() =>
  import('@/pages/admin/LoyaltyCustomerDetailPage').then((m) => ({
    default: m.LoyaltyCustomerDetailPage,
  }))
)
const LoyaltySettingsPage = lazy(() =>
  import('@/pages/admin/LoyaltySettingsPage').then((m) => ({ default: m.LoyaltySettingsPage }))
)
const IncentiveTiersPage = lazy(() =>
  import('@/pages/admin/loyalty/IncentiveTiersPage').then((m) => ({
    default: m.IncentiveTiersPage,
  }))
)
const CreateIncentiveTierPage = lazy(() =>
  import('@/pages/admin/loyalty/CreateIncentiveTierPage').then((m) => ({
    default: m.CreateIncentiveTierPage,
  }))
)
const EditIncentiveTierPage = lazy(() =>
  import('@/pages/admin/loyalty/EditIncentiveTierPage').then((m) => ({
    default: m.EditIncentiveTierPage,
  }))
)
const LoyaltyRanksPage = lazy(() =>
  import('@/pages/admin/loyalty/LoyaltyRanksPage').then((m) => ({
    default: m.LoyaltyRanksPage,
  }))
)
const CreateLoyaltyRankPage = lazy(() =>
  import('@/pages/admin/loyalty/CreateLoyaltyRankPage').then((m) => ({
    default: m.CreateLoyaltyRankPage,
  }))
)
const LoyaltyRankDetailPage = lazy(() =>
  import('@/pages/admin/loyalty/LoyaltyRankDetailPage').then((m) => ({
    default: m.LoyaltyRankDetailPage,
  }))
)
const LoyaltyCustomersPage = lazy(() =>
  import('@/pages/admin/LoyaltyCustomersPage').then((m) => ({ default: m.LoyaltyCustomersPage }))
)
const MessagesPage = lazy(() =>
  import('@/pages/admin/MessagesPage').then((m) => ({ default: m.MessagesPage }))
)
const PromotionsPage = lazy(() =>
  import('@/pages/admin/PromotionsPage').then((m) => ({ default: m.PromotionsPage }))
)
const CreatePromotionPage = lazy(() =>
  import('@/pages/admin/CreatePromotionPage').then((m) => ({ default: m.CreatePromotionPage }))
)
const EditPromotionPage = lazy(() =>
  import('@/pages/admin/EditPromotionPage').then((m) => ({ default: m.EditPromotionPage }))
)
const PromotionDetailPage = lazy(() =>
  import('@/pages/admin/PromotionDetailPage').then((m) => ({ default: m.PromotionDetailPage }))
)
const InventoryAuditsPage = lazy(() =>
  import('@/pages/admin/inventory/InventoryAuditsPage').then((m) => ({
    default: m.InventoryAuditsPage,
  }))
)
const InventoryAuditDetailPage = lazy(() =>
  import('@/pages/admin/inventory/InventoryAuditDetailPage').then((m) => ({
    default: m.InventoryAuditDetailPage,
  }))
)
const InventoryForLocationPage = lazy(() =>
  import('@/pages/admin/inventory/InventoryForLocationPage').then((m) => ({
    default: m.InventoryForLocationPage,
  }))
)
const IncomingTransfersPage = lazy(() =>
  import('@/pages/admin/inventory/TransferListPage').then((m) => ({
    default: m.IncomingTransfersPage,
  }))
)
const ReceivedTransfersPage = lazy(() =>
  import('@/pages/admin/inventory/TransferListPage').then((m) => ({
    default: m.ReceivedTransfersPage,
  }))
)
const TransferAuditsPage = lazy(() =>
  import('@/pages/admin/inventory/TransferAuditsPage').then((m) => ({
    default: m.TransferAuditsPage,
  }))
)
const TransferAuditDetailPage = lazy(() =>
  import('@/pages/admin/inventory/TransferAuditDetailPage').then((m) => ({
    default: m.TransferAuditDetailPage,
  }))
)
const MissingScansPage = lazy(() =>
  import('@/pages/admin/inventory/MissingScansPage').then((m) => ({
    default: m.MissingScansPage,
  }))
)
const ReportJobsPage = lazy(() =>
  import('@/pages/reports/ReportJobsPage').then((m) => ({ default: m.ReportJobsPage }))
)
const FindCollectionsPage = lazy(() =>
  import('@/pages/admin/inventory/FindCollectionsPage').then((m) => ({
    default: m.FindCollectionsPage,
  }))
)
const CollectionDetailPage = lazy(() =>
  import('@/pages/admin/inventory/CollectionDetailPage').then((m) => ({
    default: m.CollectionDetailPage,
  }))
)
const TransferDetailPage = lazy(() =>
  import('@/pages/admin/inventory/TransferDetailPage').then((m) => ({
    default: m.TransferDetailPage,
  }))
)
const ProductTypesPage = lazy(() =>
  import('@/pages/admin/locations/ProductTypesPage').then((m) => ({
    default: m.ProductTypesPage,
  }))
)
const CreateProductTypePage = lazy(() =>
  import('@/pages/admin/locations/CreateProductTypePage').then((m) => ({
    default: m.CreateProductTypePage,
  }))
)
const EditProductTypePage = lazy(() =>
  import('@/pages/admin/locations/EditProductTypePage').then((m) => ({
    default: m.EditProductTypePage,
  }))
)
const PriceLevelsPage = lazy(() =>
  import('@/pages/admin/locations/PriceLevelsPage').then((m) => ({
    default: m.PriceLevelsPage,
  }))
)
const CreatePriceLevelPage = lazy(() =>
  import('@/pages/admin/locations/CreatePriceLevelPage').then((m) => ({
    default: m.CreatePriceLevelPage,
  }))
)
const EditPriceLevelPage = lazy(() =>
  import('@/pages/admin/locations/EditPriceLevelPage').then((m) => ({
    default: m.EditPriceLevelPage,
  }))
)
const CompaniesPage = lazy(() =>
  import('@/pages/admin/locations/CompaniesPage').then((m) => ({ default: m.CompaniesPage }))
)
const CreateCompanyPage = lazy(() =>
  import('@/pages/admin/locations/CreateCompanyPage').then((m) => ({
    default: m.CreateCompanyPage,
  }))
)
const EditCompanyPage = lazy(() =>
  import('@/pages/admin/locations/EditCompanyPage').then((m) => ({ default: m.EditCompanyPage }))
)
const CompanyDetailPage = lazy(() =>
  import('@/pages/admin/locations/CompanyDetailPage').then((m) => ({
    default: m.CompanyDetailPage,
  }))
)
const DistrictsPage = lazy(() =>
  import('@/pages/admin/locations/DistrictsPage').then((m) => ({ default: m.DistrictsPage }))
)
const CreateDistrictPage = lazy(() =>
  import('@/pages/admin/locations/CreateDistrictPage').then((m) => ({
    default: m.CreateDistrictPage,
  }))
)
const EditDistrictPage = lazy(() =>
  import('@/pages/admin/locations/EditDistrictPage').then((m) => ({
    default: m.EditDistrictPage,
  }))
)
const LocationTypesPage = lazy(() =>
  import('@/pages/admin/locations/LocationTypesPage').then((m) => ({
    default: m.LocationTypesPage,
  }))
)
const CreateLocationTypePage = lazy(() =>
  import('@/pages/admin/locations/CreateLocationTypePage').then((m) => ({
    default: m.CreateLocationTypePage,
  }))
)
const EditLocationTypePage = lazy(() =>
  import('@/pages/admin/locations/EditLocationTypePage').then((m) => ({
    default: m.EditLocationTypePage,
  }))
)
const AdditionalTaxesPage = lazy(() =>
  import('@/pages/admin/locations/AdditionalTaxesPage').then((m) => ({
    default: m.AdditionalTaxesPage,
  }))
)
const CreateAdditionalTaxPage = lazy(() =>
  import('@/pages/admin/locations/CreateAdditionalTaxPage').then((m) => ({
    default: m.CreateAdditionalTaxPage,
  }))
)
const EditAdditionalTaxPage = lazy(() =>
  import('@/pages/admin/locations/EditAdditionalTaxPage').then((m) => ({
    default: m.EditAdditionalTaxPage,
  }))
)
const DBAsPage = lazy(() =>
  import('@/pages/admin/locations/DBAsPage').then((m) => ({ default: m.DBAsPage }))
)
const LocationDetailPage = lazy(() =>
  import('@/pages/admin/LocationDetailPage').then((m) => ({ default: m.LocationDetailPage }))
)
const CreateLocationPage = lazy(() =>
  import('@/pages/admin/CreateLocationPage').then((m) => ({ default: m.CreateLocationPage }))
)
const EditLocationPage = lazy(() =>
  import('@/pages/admin/EditLocationPage').then((m) => ({ default: m.EditLocationPage }))
)
const CreateDBAPage = lazy(() =>
  import('@/pages/admin/locations/CreateDBAPage').then((m) => ({ default: m.CreateDBAPage }))
)
const EditDBAPage = lazy(() =>
  import('@/pages/admin/locations/EditDBAPage').then((m) => ({ default: m.EditDBAPage }))
)
const DBADetailPage = lazy(() =>
  import('@/pages/admin/locations/DBADetailPage').then((m) => ({ default: m.DBADetailPage }))
)
const InventoryLogPage = lazy(() =>
  import('@/pages/reports/InventoryLogPage').then((m) => ({ default: m.InventoryLogPage }))
)
const TransferLookupPage = lazy(() =>
  import('@/pages/reports/TransferLookupPage').then((m) => ({ default: m.TransferLookupPage }))
)
const CombinedSalesReportPage = lazy(() =>
  import('@/pages/reports/CombinedSalesReportPage').then((m) => ({
    default: m.CombinedSalesReportPage,
  }))
)
const ItemSalesReportPage = lazy(() =>
  import('@/pages/reports/ItemSalesReportPage').then((m) => ({
    default: m.ItemSalesReportPage,
  }))
)
const SalesComparisonReportPage = lazy(() =>
  import('@/pages/reports/SalesComparisonReportPage').then((m) => ({
    default: m.SalesComparisonReportPage,
  }))
)
const PreSettlementReportPage = lazy(() =>
  import('@/pages/reports/PreSettlementReportPage').then((m) => ({
    default: m.PreSettlementReportPage,
  }))
)
const SettlementReportPage = lazy(() =>
  import('@/pages/reports/SettlementReportPage').then((m) => ({
    default: m.SettlementReportPage,
  }))
)
const SalesSummaryReportPage = lazy(() =>
  import('@/pages/reports/SalesSummaryReportPage').then((m) => ({
    default: m.SalesSummaryReportPage,
  }))
)
const DiscountReportPage = lazy(() =>
  import('@/pages/reports/DiscountReportPage').then((m) => ({
    default: m.DiscountReportPage,
  }))
)
const TransactionLogReportPage = lazy(() =>
  import('@/pages/reports/TransactionLogReportPage').then((m) => ({
    default: m.TransactionLogReportPage,
  }))
)
const GiftCardRedemptionReportPage = lazy(() =>
  import('@/pages/reports/GiftCardRedemptionReportPage').then((m) => ({
    default: m.GiftCardRedemptionReportPage,
  }))
)
const DiscountsPage = lazy(() =>
  import('@/pages/admin/DiscountsPage').then((m) => ({ default: m.DiscountsPage }))
)
const LocationsPage = lazy(() =>
  import('@/pages/admin/LocationsPage').then((m) => ({ default: m.LocationsPage }))
)
const PosUsersPage = lazy(() =>
  import('@/pages/admin/PosUsersPage').then((m) => ({ default: m.PosUsersPage }))
)
const ProductsPage = lazy(() =>
  import('@/pages/admin/products/ProductsPage').then((m) => ({ default: m.ProductsPage }))
)
const CreateProductPage = lazy(() =>
  import('@/pages/admin/products/CreateProductPage').then((m) => ({
    default: m.CreateProductPage,
  }))
)
const ProductDetailPage = lazy(() =>
  import('@/pages/admin/products/ProductDetailPage').then((m) => ({
    default: m.ProductDetailPage,
  }))
)
const EditProductPage = lazy(() =>
  import('@/pages/admin/products/EditProductPage').then((m) => ({ default: m.EditProductPage }))
)
const CreateProductUnitPage = lazy(() =>
  import('@/pages/admin/products/CreateProductUnitPage').then((m) => ({
    default: m.CreateProductUnitPage,
  }))
)
const EditProductUnitPage = lazy(() =>
  import('@/pages/admin/products/EditProductUnitPage').then((m) => ({
    default: m.EditProductUnitPage,
  }))
)
const SkusPage = lazy(() =>
  import('@/pages/admin/products/SkusPage').then((m) => ({ default: m.SkusPage }))
)
const CreateSkuPage = lazy(() =>
  import('@/pages/admin/products/CreateSkuPage').then((m) => ({ default: m.CreateSkuPage }))
)
const EditSkuPage = lazy(() =>
  import('@/pages/admin/products/EditSkuPage').then((m) => ({ default: m.EditSkuPage }))
)
const ProductCategoriesPage = lazy(() =>
  import('@/pages/admin/products/ProductCategoriesPage').then((m) => ({
    default: m.ProductCategoriesPage,
  }))
)
const CreateProductCategoryPage = lazy(() =>
  import('@/pages/admin/products/CreateProductCategoryPage').then((m) => ({
    default: m.CreateProductCategoryPage,
  }))
)
const EditProductCategoryPage = lazy(() =>
  import('@/pages/admin/products/EditProductCategoryPage').then((m) => ({
    default: m.EditProductCategoryPage,
  }))
)
const UsersPage = lazy(() =>
  import('@/pages/operations/UsersPage').then((m) => ({ default: m.UsersPage }))
)

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary fallback={(error, reset) => <LazyErrorFallback error={error} onRetry={reset} />}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

function PromotionRoutes() {
  return (
    <>
      <Route
        path="/admin/promotions"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_promotions_r'] }}>
            <LazyPage>
              <PromotionsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promotions/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_promotions_w'] }}>
            <LazyPage>
              <CreatePromotionPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promotions/:id"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_promotions_r'] }}>
            <LazyPage>
              <PromotionDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promotions/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_promotions_w'] }}>
            <LazyPage>
              <EditPromotionPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function InventoryAuditRoutes() {
  return (
    <>
      <Route
        path="/admin/inventory/audits"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <InventoryAuditsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inventory/audits/:location"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <InventoryAuditDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inventory/location"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <InventoryForLocationPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inventory/collections"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <FindCollectionsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inventory/collections/:collectionId"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <CollectionDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

// Transfer Audits and Missing Scans, the two review pages of web-portal#71.
function InventoryReviewRoutes() {
  return (
    <>
      <Route
        path={TRANSFER_AUDITS_PATH}
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <TransferAuditsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${TRANSFER_AUDITS_PATH}/:transferId/:auditUuid`}
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <TransferAuditDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path={MISSING_SCANS_PATH}
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <MissingScansPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

// Each list has its own detail route so the sidebar and Back follow the list
// a transfer was opened from (web-portal#94).
function InventoryTransferRoutes() {
  return (
    <>
      <Route
        path={INCOMING_TRANSFERS_PATH}
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <IncomingTransfersPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${INCOMING_TRANSFERS_PATH}/:transferId`}
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <TransferDetailPage kind="incoming" />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path={RECEIVED_TRANSFERS_PATH}
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <ReceivedTransfersPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${RECEIVED_TRANSFERS_PATH}/:transferId`}
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_inventory_r'] }}>
            <LazyPage>
              <TransferDetailPage kind="received" />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function DiscountRoutes() {
  return (
    <>
      <Route
        path="/admin/discounts"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_discounts_r'] }}>
            <LazyPage>
              <DiscountsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/discounts/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_discounts_w'] }}>
            <LazyPage>
              <CreateDiscountPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/discounts/:type/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_discounts_w'] }}>
            <LazyPage>
              <EditDiscountPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/discounts/:type/:id/duplicate"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_discounts_w'] }}>
            <LazyPage>
              <CreateDiscountPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function SkuRoutes() {
  return (
    <>
      <Route
        path="/admin/products/skus"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_r'] }}>
            <LazyPage>
              <SkusPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/skus/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <CreateSkuPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/skus/:type/:sku/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <EditSkuPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function ProductUnitRoutes() {
  return (
    <>
      <Route
        path="/admin/products/units/:type/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <EditProductUnitPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function ProductCategoryRoutes() {
  return (
    <>
      <Route
        path="/admin/products/categories"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_r'] }}>
            <LazyPage>
              <ProductCategoriesPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/categories/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <CreateProductCategoryPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/categories/:type/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <EditProductCategoryPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function ProductDetailRoutes() {
  return (
    <>
      <Route
        path="/admin/products/:type/:id"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_r'] }}>
            <LazyPage>
              <ProductDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/:type/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <EditProductPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/:type/:id/units/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <CreateProductUnitPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function ProductsRoutes() {
  return (
    <>
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_r'] }}>
            <LazyPage>
              <ProductsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <CreateProductPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      {ProductDetailRoutes()}
      {ProductUnitRoutes()}
      {ProductCategoryRoutes()}
      {SkuRoutes()}
      <Route
        path="/admin/products/product-types"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_r'] }}>
            <LazyPage>
              <ProductTypesPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/product-types/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <CreateProductTypePage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/product-types/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_products_w'] }}>
            <LazyPage>
              <EditProductTypePage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function PriceLevelRoutes() {
  return (
    <>
      <Route
        path="/admin/locations/price-levels"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <PriceLevelsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/price-levels/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <CreatePriceLevelPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/price-levels/:name/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <EditPriceLevelPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function CompanyRoutes() {
  return (
    <>
      <Route
        path="/admin/locations/companies"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <CompaniesPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/companies/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <CreateCompanyPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/companies/:id"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <CompanyDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/companies/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <EditCompanyPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function DistrictRoutes() {
  return (
    <>
      <Route
        path="/admin/locations/districts"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <DistrictsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/districts/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <CreateDistrictPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/districts/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <EditDistrictPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function LocationTypeRoutes() {
  return (
    <>
      <Route
        path="/admin/locations/location-types"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <LocationTypesPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/location-types/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <CreateLocationTypePage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/location-types/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <EditLocationTypePage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function AdditionalTaxRoutes() {
  return (
    <>
      <Route
        path="/admin/locations/additional-taxes"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <AdditionalTaxesPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/additional-taxes/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <CreateAdditionalTaxPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/additional-taxes/:machineName/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <EditAdditionalTaxPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function DBARoutes() {
  return (
    <>
      <Route
        path="/admin/locations/dbas"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <DBAsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/dbas/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <CreateDBAPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/dbas/:id"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <DBADetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/dbas/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <EditDBAPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function LocationsRoutes() {
  return (
    <>
      <Route
        path="/admin/locations"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <LocationsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <CreateLocationPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/:id"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_r'] }}>
            <LazyPage>
              <LocationDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/locations/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_locations_w'] }}>
            <LazyPage>
              <EditLocationPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      {PriceLevelRoutes()}
      {CompanyRoutes()}
      {DistrictRoutes()}
      {LocationTypeRoutes()}
      {AdditionalTaxRoutes()}
      {DBARoutes()}
    </>
  )
}

function AdminRoutes() {
  return (
    <>
      <Route
        path="/admin/messages"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_messages_r'] }}>
            <LazyPage>
              <MessagesPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/messages/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_messages_w'] }}>
            <LazyPage>
              <CreateMessagePage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      {LocationsRoutes()}
      {ProductsRoutes()}
      {InventoryAuditRoutes()}
      {InventoryTransferRoutes()}
      {InventoryReviewRoutes()}
      {DiscountRoutes()}
      {PromotionRoutes()}
      <Route
        path="/admin/pos-users"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_pos_users_r'], strict: true }}>
            <LazyPage>
              <PosUsersPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pos-users/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_pos_users_w'], strict: true }}>
            <CreatePosUserPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pos-users/:id"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_pos_users_r'], strict: true }}>
            <PosUserDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pos-users/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_pos_users_w'], strict: true }}>
            <EditPosUserPage />
          </ProtectedRoute>
        }
      />
      {LoyaltyRoutes()}
    </>
  )
}

// The loyalty configuration pages: Incentive Tiers and Loyalty Ranks.
function LoyaltyConfigRoutes() {
  return (
    <>
      <Route
        path="/admin/loyalty/incentive-tiers"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_loyalty_r'] }}>
            <LazyPage>
              <IncentiveTiersPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty/incentive-tiers/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_loyalty_w'] }}>
            <LazyPage>
              <CreateIncentiveTierPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty/incentive-tiers/:minDollars/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_loyalty_w'] }}>
            <LazyPage>
              <EditIncentiveTierPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty/ranks"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_loyalty_r'] }}>
            <LazyPage>
              <LoyaltyRanksPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty/ranks/create"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_loyalty_w'] }}>
            <LazyPage>
              <CreateLoyaltyRankPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty/ranks/:id"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_loyalty_r'] }}>
            <LazyPage>
              <LoyaltyRankDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function LoyaltyRoutes() {
  return (
    <>
      <Route
        path="/admin/loyalty"
        element={
          <ProtectedRoute permissions={{ requiredSections: LOYALTY_VIEW_SECTIONS }}>
            <LazyPage>
              <LoyaltyCustomersPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty/settings"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_loyalty_r'] }}>
            <LazyPage>
              <LoyaltySettingsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      {/* api-core gates tier reads on cs_loyalty_r alone, unlike the customer
          pages, so LOYALTY_VIEW_SECTIONS would admit users it then refuses. */}
      {LoyaltyConfigRoutes()}
      <Route
        path="/admin/loyalty/:id"
        element={
          <ProtectedRoute permissions={{ requiredSections: LOYALTY_VIEW_SECTIONS }}>
            <LazyPage>
              <LoyaltyCustomerDetailPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty/:id/edit"
        element={
          <ProtectedRoute permissions={{ requiredSections: LOYALTY_EDIT_SECTIONS }}>
            <LazyPage>
              <EditLoyaltyCustomerPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

// Pre-Settlement and Settlement both report on a location's zero day.
function SettlementReportRoutes() {
  return (
    <>
      <Route
        path="/reports/sales/pre-settlement"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <PreSettlementReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/sales/settlement"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <SettlementReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function SalesReportRoutes() {
  return (
    <>
      <Route
        path="/reports/sales/combined"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <CombinedSalesReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/sales/items"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <ItemSalesReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/sales/comparison"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <SalesComparisonReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      {SettlementReportRoutes()}
      <Route
        path="/reports/sales/gift-card"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <GiftCardRedemptionReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/sales/summary"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <SalesSummaryReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/sales/discounts"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <DiscountReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/sales/transaction-log"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_sales_r'] }}>
            <LazyPage>
              <TransactionLogReportPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function InventoryReportRoutes() {
  return (
    <>
      <Route
        path="/reports/inventory/transfer-lookup"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_inventory_r'] }}>
            <LazyPage>
              <TransferLookupPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/inventory/log"
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_inventory_r'] }}>
            <LazyPage>
              <InventoryLogPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path={REPORT_JOBS_PATH}
        element={
          <ProtectedRoute permissions={{ requiredSections: ['cs_reports_jobs_r'] }}>
            <LazyPage>
              <ReportJobsPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
    </>
  )
}

function OperationsRoutes() {
  return (
    <>
      <Route
        path="/operations/users"
        element={
          <ProtectedRoute permissions={{ requireSysOp: true }}>
            <LazyPage>
              <UsersPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/users/create"
        element={
          <ProtectedRoute permissions={{ requireSysOp: true }}>
            <CreateUserPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/users/:id/edit"
        element={
          <ProtectedRoute permissions={{ requireSysOp: true }}>
            <EditUserPage />
          </ProtectedRoute>
        }
      />
    </>
  )
}

function ProtectedPageRoutes() {
  return (
    <Route
      element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }
    >
      <Route path="/" element={<HomePage />} />
      {AdminRoutes()}
      {SalesReportRoutes()}
      {InventoryReportRoutes()}
      {OperationsRoutes()}
    </Route>
  )
}

function App() {
  const auth = useAuth()
  const { loadUser, logout } = useAuthStore()

  useEffect(() => {
    setOnUnauthorized(() => {
      useAuthStore.getState().setSessionExpired()
    })
  }, [])

  useEffect(() => {
    void loadUser()
  }, [loadUser])

  useEffect(() => {
    if (auth.error?.message === 'login_required') {
      logout()
    }
  }, [auth.error, logout])

  return (
    <BrowserRouter>
      <TokenSync />
      <TokenMonitor />
      <SessionExpiredDialog />
      <SessionExpiryWarningDialog />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/select-system" element={<SystemSelectionPage />} />
        <Route path="/debug" element={<DebugPage />} />

        {ProtectedPageRoutes()}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
